import { useCallback, useEffect, useState } from "react";

import { useActionHook, usePrompt } from "hooks";
import { useNewsFeedActions } from "store";
import type { SourceRow, TopicRow } from "store/types/news";

import { ConfigureFeedDialog } from "../components/ConfigureFeedDialog/ConfigureFeedDialog";
import type { FeedConfigBuckets } from "../context/NewsFeedContext";

export type FeedConfig = {
  topics: FeedConfigBuckets<TopicRow>;
  sources: FeedConfigBuckets<SourceRow>;
  guidance: string;
};

const EMPTY_FEED_CONFIG: FeedConfig = {
  topics: { system: [], user: [] },
  sources: { system: [], user: [] },
  guidance: ""
};

export interface UseFeedConfigSettingsResult {
  feedConfig: FeedConfig;
  openConfigureFeed: () => Promise<void>;
}

export function useFeedConfigSettings(): UseFeedConfigSettingsResult {
  const {
    getFeedConfigAction,
    getGuidancePromptAction,
    CreateTopicsAction,
    UpdateTopicsAction,
    DeleteTopicsAction,
    UpdateTopicsSelectAction,
    RemoveTopicsSelectedAction,
    CreateSourcesAction,
    UpdateSourcesSelectAction,
    DeleteSourcesAction,
    RemoveSourcesSelectedAction
  } = useNewsFeedActions();
  const openPrompt = usePrompt();

  const [feedConfig, setFeedConfig] = useState<FeedConfig>(EMPTY_FEED_CONFIG);

  const { action: getFeedConfig } = useActionHook(getFeedConfigAction, {
    onSuccess: (data) => {
      setFeedConfig({
        topics: data.topics,
        sources: data.sources,
        guidance: data.guidance_prompt
      });
    }
  });

  useEffect(() => {
    getFeedConfig();
  }, []);

  const createTopic = useCallback(
    async (payload: TopicRow) => {
      const created = await CreateTopicsAction(payload);
      setFeedConfig((prev) => ({
        ...prev,
        topics: { ...prev.topics, user: [...prev.topics.user, created] }
      }));
      return created;
    },
    [CreateTopicsAction]
  );

  const updateTopic = useCallback(
    async (payload: TopicRow) => {
      const updated = await UpdateTopicsAction(payload);
      setFeedConfig((prev) => ({
        ...prev,
        topics: {
          ...prev.topics,
          user: prev.topics.user.map((t) => (t.id === updated.id ? updated : t))
        }
      }));
      return updated;
    },
    [UpdateTopicsAction]
  );

  const deleteTopic = useCallback(
    async (id: number) => {
      await DeleteTopicsAction(id);
      setFeedConfig((prev) => ({
        ...prev,
        topics: { ...prev.topics, user: prev.topics.user.filter((t) => t.id !== id) }
      }));
    },
    [DeleteTopicsAction]
  );

  const selectSystemTopic = useCallback(
    async (id: number) => {
      await UpdateTopicsSelectAction({ id });
      setFeedConfig((prev) => {
        const item = prev.topics.system.find((t) => t.id === id);
        if (!item) return prev;
        return {
          ...prev,
          topics: {
            system: prev.topics.system.filter((t) => t.id !== id),
            user: [...prev.topics.user, item]
          }
        };
      });
    },
    [UpdateTopicsSelectAction]
  );

  const unselectSystemTopic = useCallback(
    async (id: number) => {
      await RemoveTopicsSelectedAction(id);
      setFeedConfig((prev) => {
        const item = prev.topics.user.find((t) => t.id === id);
        if (!item) return prev;
        return {
          ...prev,
          topics: {
            system: [...prev.topics.system, item],
            user: prev.topics.user.filter((t) => t.id !== id)
          }
        };
      });
    },
    [RemoveTopicsSelectedAction]
  );

  const createSource = useCallback(
    async (payload: SourceRow) => {
      const created = await CreateSourcesAction(payload);
      setFeedConfig((prev) => ({
        ...prev,
        sources: { ...prev.sources, user: [...prev.sources.user, created] }
      }));
      return created;
    },
    [CreateSourcesAction]
  );

  const updateSourcePriority = useCallback(
    async (source_url: string, priority: number, name: string) => {
      const updated = await UpdateSourcesSelectAction({ source_url, priority, name });
      setFeedConfig((prev) => ({
        ...prev,
        sources: {
          ...prev.sources,
          user: prev.sources.user.map((s) => (s.source_url === updated.source_url ? { ...s, ...updated } : s))
        }
      }));
      return updated;
    },
    [UpdateSourcesSelectAction]
  );

  const deleteSource = useCallback(
    async (source_url: string) => {
      await DeleteSourcesAction(source_url);
      setFeedConfig((prev) => ({
        ...prev,
        sources: { ...prev.sources, user: prev.sources.user.filter((s) => s.source_url !== source_url) }
      }));
    },
    [DeleteSourcesAction]
  );

  const selectSystemSource = useCallback(
    async (source_url: string, name: string) => {
      const priority = feedConfig.sources.user.length + 1;
      await UpdateSourcesSelectAction({ source_url, priority, name });
      setFeedConfig((prev) => {
        const item = prev.sources.system.find((s) => s.source_url === source_url);
        if (!item) return prev;
        return {
          ...prev,
          sources: {
            system: prev.sources.system.filter((s) => s.source_url !== source_url),
            user: [...prev.sources.user, { ...item, priority }]
          }
        };
      });
    },
    [UpdateSourcesSelectAction, feedConfig.sources.user]
  );

  const unselectSystemSource = useCallback(
    async (source_url: string) => {
      await RemoveSourcesSelectedAction(source_url);
      setFeedConfig((prev) => {
        const item = prev.sources.user.find((s) => s.source_url === source_url);
        if (!item) return prev;
        return {
          ...prev,
          sources: {
            system: [...prev.sources.system, { ...item, priority: null }],
            user: prev.sources.user.filter((s) => s.source_url !== source_url)
          }
        };
      });
    },
    [RemoveSourcesSelectedAction]
  );

  const persistGuidance = useCallback(
    async (nextGuidance: string) => {
      await getGuidancePromptAction(nextGuidance);
      setFeedConfig((prev) => ({ ...prev, guidance: nextGuidance }));
    },
    [getGuidancePromptAction]
  );

  const openConfigureFeed = useCallback(async () => {
    const previous = feedConfig;
    const result = await openPrompt({
      componentType: "modal",
      Component: ConfigureFeedDialog,
      props: {
        topics: feedConfig.topics,
        sources: feedConfig.sources,
        guidance: feedConfig.guidance,
        onSaveGuidance: persistGuidance,
        onCreateTopic: createTopic,
        onUpdateTopic: updateTopic,
        onDeleteTopic: deleteTopic,
        onSelectSystemTopic: selectSystemTopic,
        onUnselectSystemTopic: unselectSystemTopic,
        onCreateSource: createSource,
        onUpdateSourcePriority: updateSourcePriority,
        onDeleteSource: deleteSource,
        onSelectSystemSource: selectSystemSource,
        onUnselectSystemSource: unselectSystemSource
      }
    });
    if (!result) return;

    if (result.guidance !== previous.guidance) {
      await getGuidancePromptAction(result.guidance);
      setFeedConfig((prev) => ({ ...prev, guidance: result.guidance }));
    }
  }, [
    openPrompt,
    feedConfig,
    getGuidancePromptAction,
    persistGuidance,
    createTopic,
    updateTopic,
    deleteTopic,
    selectSystemTopic,
    unselectSystemTopic,
    createSource,
    updateSourcePriority,
    deleteSource,
    selectSystemSource,
    unselectSystemSource
  ]);

  return { feedConfig, openConfigureFeed };
}
